import { useState, useEffect, useCallback } from 'react';
import { X, Key, Plus, Trash2, RefreshCw, Eye, EyeOff, CheckCircle2, AlertCircle, Shield, TestTube } from 'lucide-react';
import { API_URL } from '../config';

interface Credential {
  id: string;
  name: string;
  type: string;
  template?: string;
  description?: string;
  icon?: string;
  created_at: string;
  updated_at: string;
  last_used_at?: string;
  use_count: number;
}

interface CredentialTemplate {
  type: string;
  fields: string[];
  icon: string;
  name: string;
  api_key_header?: string;
  api_key_prefix?: string;
  test_url?: string;
}

interface CredentialsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
}

export default function CredentialsPanel({
  isOpen,
  onClose,
  userId = 'default',
}: CredentialsPanelProps) {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [templates, setTemplates] = useState<Record<string, CredentialTemplate>>({});
  const [activeTab, setActiveTab] = useState<'list' | 'create'>('list');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{id: string; success: boolean; message: string} | null>(null);
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState<string>('custom');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyHeader, setApiKeyHeader] = useState('Authorization');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpSsl, setSmtpSsl] = useState(false);
  const [dbHost, setDbHost] = useState('');
  const [dbPort, setDbPort] = useState(5432);
  const [dbName, setDbName] = useState('');
  const [dbUser, setDbUser] = useState('');
  const [dbPassword, setDbPassword] = useState('');
  const [showSecrets, setShowSecrets] = useState(false);

  const loadCredentials = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/credentials/list?owner_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setCredentials(data);
      }
    } catch (e) {
      console.error('Error loading credentials:', e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/credentials/templates`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (e) {
      console.error('Error loading templates:', e);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadCredentials();
      loadTemplates();
    }
  }, [isOpen, loadCredentials, loadTemplates]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setApiKey('');
    setApiKeyHeader('Authorization');
    setClientId('');
    setClientSecret('');
    setSmtpHost('');
    setSmtpPort(587);
    setSmtpUser('');
    setSmtpPassword('');
    setSmtpSsl(false);
    setDbHost('');
    setDbPort(5432);
    setDbName('');
    setDbUser('');
    setDbPassword('');
  };

  const createCredential = async () => {
    if (!name.trim()) {
      setError('Il nome è obbligatorio');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const template = templates[selectedTemplate];
      const credType = template?.type || 'custom';

      const body: any = {
        name: name.trim(),
        type: credType,
        description: description || undefined,
      };

      // Aggiungi campi in base al tipo
      if (credType === 'api_key' || selectedTemplate === 'custom') {
        if (apiKey) body.api_key = apiKey;
        if (apiKeyHeader) body.api_key_header = apiKeyHeader;
      }
      
      if (credType === 'oauth2') {
        if (clientId) body.client_id = clientId;
        if (clientSecret) body.client_secret = clientSecret;
      }
      
      if (credType === 'smtp') {
        body.smtp_host = smtpHost;
        body.smtp_port = smtpPort;
        body.smtp_user = smtpUser;
        body.smtp_password = smtpPassword;
        body.smtp_ssl = smtpSsl;
      }
      
      if (credType === 'database') {
        body.db_host = dbHost;
        body.db_port = dbPort;
        body.db_name = dbName;
        body.db_user = dbUser;
        body.db_password = dbPassword;
      }

      const res = await fetch(`${API_URL}/credentials/create?owner_id=${userId}&template=${selectedTemplate}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setActiveTab('list');
        resetForm();
        loadCredentials();
      } else {
        const data = await res.json();
        setError(data.detail || 'Errore creazione credenziale');
      }
    } catch (e) {
      setError('Errore di rete');
    } finally {
      setLoading(false);
    }
  };

  const deleteCredential = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa credenziale?')) return;
    
    try {
      await fetch(`${API_URL}/credentials/${id}`, { method: 'DELETE' });
      loadCredentials();
    } catch (e) {
      console.error('Error deleting credential:', e);
    }
  };

  const testCredential = async (id: string) => {
    try {
      setTestingId(id);
      setTestResult(null);
      
      const res = await fetch(`${API_URL}/credentials/${id}/test`, { method: 'POST' });
      const data = await res.json();
      
      setTestResult({ id, success: data.success, message: data.message });
    } catch (e) {
      setTestResult({ id, success: false, message: 'Errore nel test' });
    } finally {
      setTestingId(null);
    }
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('it-IT');
  };

  const getSelectedTemplate = () => templates[selectedTemplate];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[900px] max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <Shield className="w-6 h-6" />
            <h2 className="text-xl font-bold">Credentials Vault</h2>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-6 py-3 font-medium ${activeTab === 'list' ? 'text-emerald-600 border-b-2 border-emerald-500' : 'text-gray-500'}`}
          >
            🔐 Credenziali ({credentials.length})
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`px-6 py-3 font-medium ${activeTab === 'create' ? 'text-emerald-600 border-b-2 border-emerald-500' : 'text-gray-500'}`}
          >
            <Plus className="w-4 h-4 inline mr-1" />
            Nuova Credenziale
          </button>
          <div className="flex-1" />
          <button onClick={loadCredentials} className="px-4 text-gray-500 hover:text-emerald-600">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
              <button onClick={() => setError(null)} className="ml-auto">×</button>
            </div>
          )}

          {/* List Tab */}
          {activeTab === 'list' && (
            <div className="space-y-4">
              {credentials.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Key className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna credenziale salvata</p>
                  <button 
                    onClick={() => setActiveTab('create')}
                    className="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                  >
                    Aggiungi la prima credenziale
                  </button>
                </div>
              ) : (
                credentials.map(cred => (
                  <div key={cred.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{cred.icon || '🔐'}</span>
                        <div>
                          <h3 className="font-bold text-lg">{cred.name}</h3>
                          {cred.description && (
                            <p className="text-sm text-gray-600">{cred.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            Tipo: {cred.type} • Usata {cred.use_count} volte
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => testCredential(cred.id)}
                          disabled={testingId === cred.id}
                          className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 disabled:opacity-50"
                          title="Testa credenziale"
                        >
                          {testingId === cred.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <TestTube className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => deleteCredential(cred.id)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {testResult?.id === cred.id && (
                      <div className={`mt-3 p-2 rounded text-sm ${testResult.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {testResult.success ? <CheckCircle2 className="w-4 h-4 inline mr-1" /> : <AlertCircle className="w-4 h-4 inline mr-1" />}
                        {testResult.message}
                      </div>
                    )}
                    
                    <div className="mt-3 text-xs text-gray-500">
                      Creata: {formatDate(cred.created_at)} • 
                      Ultimo uso: {formatDate(cred.last_used_at)}
                    </div>
                    
                    <div className="mt-2 bg-gray-50 p-2 rounded text-xs font-mono text-gray-600">
                      Usa nel flow: <code className="bg-emerald-100 px-1 rounded">{`{{credentials.${cred.name}}}`}</code>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Create Tab */}
          {activeTab === 'create' && (
            <div className="max-w-xl mx-auto space-y-6">
              {/* Template Selection */}
              <div>
                <label className="block font-medium mb-2">Tipo Servizio</label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(templates).map(([key, template]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedTemplate(key);
                        setName(template.name);
                        if (template.api_key_header) setApiKeyHeader(template.api_key_header);
                      }}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        selectedTemplate === key 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-2xl block mb-1">{template.icon}</span>
                      <span className="text-xs font-medium">{template.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block font-medium mb-2">Nome Credenziale</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Es: my_openai_key"
                  className="w-full px-4 py-2 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usalo nei flow come: <code className="bg-gray-100 px-1 rounded">{`{{credentials.${name || 'nome'}}}`}</code>
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block font-medium mb-2">Descrizione (opzionale)</label>
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Es: Chiave per ambiente produzione"
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              {/* Dynamic Fields based on template */}
              {getSelectedTemplate()?.type === 'api_key' && (
                <div className="space-y-4 bg-emerald-50 p-4 rounded-lg">
                  <h4 className="font-bold text-emerald-800">🔑 API Key</h4>
                  <div>
                    <label className="block text-sm font-medium mb-1">API Key</label>
                    <div className="relative">
                      <input
                        type={showSecrets ? 'text' : 'password'}
                        value={apiKey}
                        onChange={e => setApiKey(e.target.value)}
                        placeholder="sk-..."
                        className="w-full px-4 py-2 border rounded-lg pr-10 font-mono"
                      />
                      <button
                        onClick={() => setShowSecrets(!showSecrets)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Header Name</label>
                    <input
                      type="text"
                      value={apiKeyHeader}
                      onChange={e => setApiKeyHeader(e.target.value)}
                      placeholder="Authorization"
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              )}

              {getSelectedTemplate()?.type === 'oauth2' && (
                <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-bold text-blue-800">🔐 OAuth2</h4>
                  <div>
                    <label className="block text-sm font-medium mb-1">Client ID</label>
                    <input
                      type="text"
                      value={clientId}
                      onChange={e => setClientId(e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Client Secret</label>
                    <div className="relative">
                      <input
                        type={showSecrets ? 'text' : 'password'}
                        value={clientSecret}
                        onChange={e => setClientSecret(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg pr-10 font-mono"
                      />
                      <button
                        onClick={() => setShowSecrets(!showSecrets)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {getSelectedTemplate()?.type === 'smtp' && (
                <div className="space-y-4 bg-rose-50 p-4 rounded-lg">
                  <h4 className="font-bold text-rose-800">📧 SMTP</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Host</label>
                      <input
                        type="text"
                        value={smtpHost}
                        onChange={e => setSmtpHost(e.target.value)}
                        placeholder="smtp.gmail.com"
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Port</label>
                      <input
                        type="number"
                        value={smtpPort}
                        onChange={e => setSmtpPort(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Username</label>
                      <input
                        type="text"
                        value={smtpUser}
                        onChange={e => setSmtpUser(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Password</label>
                      <input
                        type="password"
                        value={smtpPassword}
                        onChange={e => setSmtpPassword(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={smtpSsl}
                      onChange={e => setSmtpSsl(e.target.checked)}
                    />
                    <span className="text-sm">Usa SSL/TLS</span>
                  </label>
                </div>
              )}

              {getSelectedTemplate()?.type === 'database' && (
                <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-bold text-purple-800">🗄️ Database</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Host</label>
                      <input
                        type="text"
                        value={dbHost}
                        onChange={e => setDbHost(e.target.value)}
                        placeholder="localhost"
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Port</label>
                      <input
                        type="number"
                        value={dbPort}
                        onChange={e => setDbPort(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Database</label>
                      <input
                        type="text"
                        value={dbName}
                        onChange={e => setDbName(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Username</label>
                      <input
                        type="text"
                        value={dbUser}
                        onChange={e => setDbUser(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Password</label>
                    <input
                      type="password"
                      value={dbPassword}
                      onChange={e => setDbPassword(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              )}

              {/* Submit */}
              <button
                onClick={createCredential}
                disabled={loading || !name.trim()}
                className="w-full py-3 bg-emerald-500 text-white rounded-lg font-bold hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Salvataggio...' : '🔐 Salva Credenziale'}
              </button>

              <p className="text-xs text-center text-gray-500">
                🔒 Le credenziali sono criptate e salvate in modo sicuro.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

